// src/features/streams/components/simulcast/PlatformLogos.tsx
// Brand SVG logos for simulcast destination platforms.
// Icon variants (small) are used in cards and compact contexts.
// Lockup variants (full brand) are used in the type selector showcase.

interface LogoProps {
  size?: number;
}

// ---------------------------------------------------------------------------
// Icon variants — compact, for cards, headers, inline use
// ---------------------------------------------------------------------------

/** YouTube official red play-button icon. */
export function YouTubeLogo({ size = 32 }: LogoProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.546 12 3.546 12 3.546s-7.505 0-9.377.504A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.504 9.376.504 9.376.504s7.505 0 9.377-.504a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814Z"
        fill="#FF0000"
      />
      <path d="m9.545 15.568 6.272-3.568-6.272-3.568v7.136Z" fill="#fff" />
    </svg>
  );
}

/** Facebook official "f" circle icon in Facebook Blue. */
export function FacebookLogo({ size = 32 }: LogoProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M24 12c0-6.627-5.373-12-12-12S0 5.373 0 12c0 5.99 4.388 10.954 10.125 11.854V15.47H7.078V12h3.047V9.356c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.926-1.956 1.874V12h3.328l-.532 3.469h-2.796v8.385C19.612 22.954 24 17.99 24 12Z"
        fill="#1877F2"
      />
      <path
        d="m16.671 15.469.532-3.469H13.875v-2.25c0-.949.465-1.875 1.956-1.875h1.514V4.922s-1.374-.235-2.686-.235c-2.741 0-4.533 1.662-4.533 4.669V12H7.078v3.469h3.047v8.385a12.09 12.09 0 0 0 3.75 0V15.47h2.796Z"
        fill="#fff"
      />
    </svg>
  );
}

/** Broadcast/signal icon for Custom RTMP. */
export function RtmpLogo({ size = 32 }: LogoProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="12" cy="12" r="3" fill="#868E96" />
      <path
        d="M8.46 8.46a5 5 0 0 0 0 7.08"
        stroke="#868E96"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M15.54 8.46a5 5 0 0 1 0 7.08"
        stroke="#868E96"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M5.64 5.64a9 9 0 0 0 0 12.72"
        stroke="#868E96"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M18.36 5.64a9 9 0 0 1 0 12.72"
        stroke="#868E96"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Lockup variants — HTML/CSS compositions for the type selector showcase.
// Uses SVG icons + HTML text for reliable sizing and centering.
// ---------------------------------------------------------------------------

/** YouTube lockup: red play button + "YouTube" text. */
export function YouTubeLockup() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
      <YouTubeLogo size={36} />
      <span style={{ fontSize: 24, fontWeight: 600, color: '#212529', letterSpacing: -0.5, lineHeight: 1 }}>
        YouTube
      </span>
    </div>
  );
}

/** Facebook Live lockup: blue "f" circle + red "LIVE" badge. */
export function FacebookLiveLockup() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center' }}>
      <FacebookLogo size={44} />
      <div
        style={{
          backgroundColor: '#ED4245',
          borderRadius: 6,
          padding: '6px 16px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <span style={{ color: '#fff', fontWeight: 700, fontSize: 20, letterSpacing: 2 }}>
          LIVE
        </span>
      </div>
    </div>
  );
}

/** RTMP bold protocol text lockup. */
export function RtmpLockup() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <span style={{ fontSize: 36, fontWeight: 900, color: '#495057', letterSpacing: 5, lineHeight: 1, paddingLeft: 5 }}>
        RTMP
      </span>
    </div>
  );
}
